import PropTypes from "prop-types";
import { Group } from "@mantine/core";
import { HiOutlineFunnel } from "react-icons/hi2";
import CustomSearchBar from "../../../ui/CustomSearchBar/CustomSearchBar";
import CustomSelect from "../../../ui/CustomSelect/CustomSelect";
import CustomDateRangePicker from "../../../ui/CustomDateRangePicker/CustomDateRangePicker";
import CustomExportButton from "../../../ui/CustomExportButton/CustomExportButton";
import ColumnVisibilitySelector from "../../../ui/ColumnVisibilitySelector/ColumnVisibilitySelector";
import { CustomComingSoonCard } from "../../../ui/CustomComingSoonCard/CustomComingSoonCard";
import useColumnVisibilityStore, {
  selectVisibleColumns,
} from "../../../../stores/columnVisibilityStore";
import { parseDateRange } from "../../../Transactions/utils/transactionFormatters";
import { optionsFilterByLabel } from "../../../../utils/optionsFilter";
import useBatchTransactionStatusOptions from "../hooks/useBatchTransactionStatusOptions";

const DEFAULT_COLUMN_KEYS = ["transactionId", "recipientName", "status"];

const COLUMN_META = [
  { key: "transactionId", label: "Transaction ID" },
  { key: "batchId", label: "Batch Entry Ref" },
  { key: "recipientName", label: "Recipient" },
  { key: "amount", label: "Amount" },
  { key: "charges", label: "Charges" },
  { key: "total", label: "Total" },
  { key: "status", label: "Status" },
  { key: "createdDateTime", label: "Date" },
];

const BatchTransactionsFilters = ({
  filters,
  exportProps,
  disabled = false,
}) => {
  const {
    searchQuery,
    setSearchQuery,
    dateRange,
    setDateRange,
    status,
    setStatus,
  } = filters;
  const { handleExport, isExporting } = exportProps;

  const {
    optionsWithAll: statusOptions,
    isLoading: statusOptionsLoading,
    isError: statusOptionsError,
    hasOptions: hasStatusOptions,
  } = useBatchTransactionStatusOptions();

  let statusSelectTitle = "";
  if (statusOptionsError) {
    statusSelectTitle = "Cannot select: failed to load statuses";
  } else if (!statusOptionsLoading && !hasStatusOptions) {
    statusSelectTitle = "Cannot select: no statuses available";
  }

  const visibleColumnKeys = useColumnVisibilityStore(
    selectVisibleColumns("batchTransactions"),
  );
  const setVisibleColumns = useColumnVisibilityStore(
    (s) => s.setVisibleColumns,
  );

  return (
    <Group align="flex-start" justify="space-between" wrap="wrap">
      <Group align="flex-start" wrap="wrap">
        <CustomSearchBar
          placeholder="Search by recipient, phone, reference..."
          onSearchChange={setSearchQuery}
          value={searchQuery}
          debounceDelay={500}
          size="sm"
          inputStyle={{ width: "clamp(200px, 40vw, 360px)", minWidth: 120 }}
          style={{ flex: "1 1 auto" }}
          disabled={disabled}
        />

        <CustomSelect
          data={statusOptions}
          value={status}
          onChange={(v) => setStatus(v || "all")}
          leftSection={<HiOutlineFunnel color="gray" size={14} />}
          searchable
          comboboxProps={{ withinPortal: false }}
          filter={optionsFilterByLabel}
          placeholder="All Statuses"
          title={statusSelectTitle}
          size="sm"
          style={{ width: 160 }}
          disabled={
            disabled ||
            statusOptionsLoading ||
            statusOptionsError ||
            !hasStatusOptions
          }
        />

        <CustomDateRangePicker
          value={parseDateRange(dateRange)}
          onChange={setDateRange}
          size="sm"
          disabled={disabled}
        />
      </Group>

      <Group gap="xs" align="flex-start">
        <CustomComingSoonCard>
          <CustomExportButton
            label={isExporting ? "Exporting" : "Export Data"}
            filename="batch_transactions"
            showLoadingOverlay={false}
            disabled={disabled || isExporting}
            customExportHandlers={{
              pdf: handleExport,
              csv: () => {},
              excel: () => {},
            }}
            exportConfig={{
              csv: { disabled: true },
              excel: { disabled: true },
            }}
          />
        </CustomComingSoonCard>
        <ColumnVisibilitySelector
          columns={COLUMN_META}
          defaultColumnKeys={DEFAULT_COLUMN_KEYS}
          visibleColumnKeys={visibleColumnKeys}
          onChange={(keys) => setVisibleColumns("batchTransactions", keys)}
          buttonProps={{ disabled }}
        />
      </Group>
    </Group>
  );
};

BatchTransactionsFilters.propTypes = {
  filters: PropTypes.shape({
    searchQuery: PropTypes.string,
    setSearchQuery: PropTypes.func,
    dateRange: PropTypes.shape({
      startDate: PropTypes.string,
      endDate: PropTypes.string,
    }),
    setDateRange: PropTypes.func,
    status: PropTypes.string,
    setStatus: PropTypes.func,
  }).isRequired,
  exportProps: PropTypes.shape({
    handleExport: PropTypes.func,
    isExporting: PropTypes.bool,
  }).isRequired,
  disabled: PropTypes.bool,
};

export default BatchTransactionsFilters;
